import BaseDomain from './base_domain'
import ApiError from './api_error'

import { destroy, get, post, put } from '@/lib/verbs'

export default class Event extends BaseDomain {
  // POST /api/race/events
  async create(event, accessToken) {
    const response = await post(`${this.url}/api/v1/race/events`, {
      body: { event },
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    })
    if (response.ok) {
      return await response.json
    } else {
      throw new Error('Cannot create event')
    }
  }

  // POST /api/race/events
  async draft(accessToken) {
    const response = await post(`${this.url}/api/v1/race/events/draft`, {
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    })
    if (response.ok) {
      return await response.json
    } else {
      throw new Error('Cannot draft event')
    }
  }

  // PATCH:PUT /api/race/events/:id
  async update(event, accessToken) {
    const response = await put(`${this.url}/api/v1/race/events/${event.id}`, {
      body: { event },
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    })
    if (response.ok) {
      return await response.json
    } else {
      throw new Error('Cannot create event')
    }
  }

  // DELETE /api/race/events/:id
  async destroy(event, accessToken) {
    const response = await destroy(
      `${this.url}/api/v1/race/events/${event.id}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      }
    )
    const result = await response.json
    if (response.ok) {
      return result
    } else {
      throw new ApiError('Cannot destroy event', result)
    }
  }

  async active(params = {}, accessToken) {
    const response = await get(`${this.url}/api/v1/race/events`, {
      query: params,
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    })
    if (response.ok) {
      return await response.json
    } else {
      throw new Error('Cannot get event')
    }
  }

  async publicEvents() {
    const response = await get(`${this.url}/api/v1/race/public_events`)
    const result = await response.json
    if (response.ok) {
      return { events: result.events, pagination: result.pagination }
    } else {
      throw new Error('Cannot get event')
    }
  }

  async show(id, accessToken) {
    const response = await get(`${this.url}/api/v1/race/events/${id}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    })
    if (response.ok) {
      return await response.json
    } else {
      throw new Error('Cannot get event')
    }
  }

  async showBySlug(slug, accessToken) {
    const response = await get(`${this.url}/api/v1/race/events/slug/${slug}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    })
    if (response.ok) {
      return await response.json
    } else {
      throw new Error('Cannot get event by slug')
    }
  }

  async status(id, accessToken) {
    const response = await get(`${this.url}/api/v1/race/events/${id}/status`, {
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    })

    const result = await response.json
    if (response.ok) {
      return result
    } else {
      throw new ApiError('Not found status', result)
    }
  }

  async list(params = {}, accessToken) {
    const response = await get(`${this.url}/api/v1/race/events`, {
      headers: {
        Authorization: `Bearer ${accessToken}`
      },
      query: params
    })
    const result = await response.json
    if (response.ok) {
      return { data: result.events, pagination: result.pagination }
    } else {
      throw new Error('Cannot get event list')
    }
  }

  async report(id, accessToken) {
    const response = await get(`${this.url}/api/v1/race/events/${id}/report`, {
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    })
    if (response.ok) {
      return await response.json
    } else {
      throw new Error('Cannot get event')
    }
  }

  async registrations(id, query = {}, accessToken) {
    const response = await get(
      `${this.url}/api/v1/race/events/${id}/registrations`,
      {
        query,
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      }
    )
    const result = await response.json
    if (response.ok) {
      return result
    } else {
      throw new ApiError('Cannot get registration', result)
    }
  }

  async participants(id, query = {}, accessToken) {
    const response = await get(
      `${this.url}/api/v1/race/events/${id}/participants`,
      {
        query,
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      }
    )
    const result = await response.json
    if (response.ok) {
      return result
    } else {
      throw new ApiError('Cannot get participants', result)
    }
  }

  async participants_report(id, accessToken) {
    const response = await get(
      `${this.url}/api/v1/race/events/${id}/participants_report`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      }
    )
    if (response.ok) {
      return await response.json
    } else {
      throw new Error('Cannot get participants report')
    }
  }

  async summary(id, accessToken) {
    const response = await get(`${this.url}/api/v1/race/events/${id}/summary`, {
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    })
    if (response.ok) {
      return await response.json
    } else {
      throw new Error('Cannot get summary')
    }
  }

  async registrationsSummary(id, query, accessToken) {
    const response = await get(
      `${this.url}/api/v1/race/events/${id}/summary_registrations`,
      {
        query,
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      }
    )
    if (response.ok) {
      return await response.json
    } else {
      throw new Error('Cannot get summary')
    }
  }

  async downloadParticipants(id, accessToken) {
    const response = await get(
      `${this.url}/api/v1/race/reports/${id}/participants.xlsx`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      }
    )
    if (response.ok) {
      return await response.blob
    } else {
      throw new Error('Cannot download participants')
    }
  }

  async searchEvents(q, dateStart, dateEnd) {
    const searchUrl = `${this.url}/api/v1/race/events/search.json`
    const url = new URL(searchUrl)
    if (q) url.searchParams.set('q', q)
    if (dateStart) url.searchParams.set('date_start', dateStart)
    if (dateEnd) url.searchParams.set('date_end', dateEnd)
    const response = await get(url.toString())
    if (response.ok) {
      return await response.json
    } else {
      throw new Error('Cannot search event')
    }
  }

  async upload(body, accessToken) {
    const response = await post(`${this.url}/api/v1/race/events/upload`, {
      body,
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    })
    if (response.ok) {
      return ''
    } else {
      throw new ApiError('Cannot upload file', 'error')
    }
  }

  async downloadRegistrations(id, accessToken) {
    const response = await get(
      `${this.url}/api/v1/race/reports/${id}/registrations.xlsx`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      }
    )
    if (response.ok) {
      return await response.blob
    } else {
      throw new Error('Cannot download registrations')
    }
  }
}
